package diana.soleil.design.iraniancanadienbazzar.core.model

import kotlinx.serialization.Serializable

/**
 * A product a business sells (`businesses/{businessId}/products/{id}` — see `docs/DATA_MODEL.md`).
 * Prices are integer cents (CAD) — never floats.
 */
data class Product(
    val id: String,
    val businessId: String,
    val name: LocalizedText,
    val description: LocalizedText = LocalizedText(),
    val priceCents: Long,
    val imageUrl: String? = null,
    val available: Boolean = true,
    val promoted: Boolean = false,
    val property: PropertyDetails? = null,
)

/**
 * Optional real-estate facts on a [Product] — lets realtors/landlords post property-style listings
 * (the price stays in [Product.priceCents]). All fields optional; absent map = not a property.
 */
data class PropertyDetails(
    val bedrooms: Int? = null,
    val bathrooms: Double? = null,
    val areaSqFt: Int? = null,
    val address: String? = null,
) {
    val hasAnyFact: Boolean get() =
        bedrooms != null || bathrooms != null || areaSqFt != null || address != null
}

/**
 * One cart line. A snapshot of the product at add-time (name resolved to the active language then,
 * price frozen) — carts survive restarts via [kotlinx.serialization], so this is @Serializable.
 * The cart is **single-business** (orders are per business); see CartController.
 */
@Serializable
data class CartItem(
    val productId: String,
    val businessId: String,
    val businessName: String,
    val name: String,
    val priceCents: Long,
    val quantity: Int,
) {
    val lineTotalCents: Long get() = priceCents * quantity
}

/** An order placed for pickup (`orders/{id}`). Status/payment transitions per DATA_MODEL rules. */
data class Order(
    val id: String,
    val businessId: String,
    val businessName: String,
    val items: List<OrderItem>,
    val subtotalCents: Long,
    val totalCents: Long,
    val status: OrderStatus,
    val paymentStatus: PaymentStatus,
    val note: String = "",
    val createdAtMillis: Long? = null,
)

data class OrderItem(
    val productId: String,
    val name: String,
    val quantity: Int,
    val priceCents: Long,
)

enum class OrderStatus { Pending, Confirmed, Fulfilled, Cancelled }
enum class PaymentStatus { Unpaid, Paid, Refunded }

/** Formats integer cents as a CAD display string with thousands grouping (e.g. 98800000 → "$988,000.00"). */
fun Long.formatCadCents(): String {
    val dollars = this / 100
    val cents = (this % 100).toInt()
    val grouped = dollars.toString().reversed().chunked(3).joinToString(",").reversed()
    return "\$$grouped.${cents.toString().padStart(2, '0')}"
}
